import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    Theme,
    CircularProgress,
} from '@material-ui/core'
import EventNoteIcon from '@material-ui/icons/EventNote'
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import SearchIcon from '@material-ui/icons/Search'
import AnnouncementOutlinedIcon from '@material-ui/icons/AnnouncementOutlined'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import TuneIcon from '@material-ui/icons/Tune'
// import SettingsIcon from '@material-ui/icons/Settings'
import SchoolIcon from '@material-ui/icons/School'
import ContactMailIcon from '@material-ui/icons/ContactMail'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import HotelIcon from '@material-ui/icons/Hotel'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { Routes } from '../../navigation/config/routes'
import { useUser } from '../../hooks/useUser'
import { useLeasingSeason } from './hooks/useLeasingSeason'
import { CollegesYouServe } from './CollegesYouServe/CollegesYouServe'
import { LeadsPipelineHome } from './LeadsPipeline/LeadsPipelineHome'
import { LeasingSeasonOutreach } from './Outreach/LeasingSeasonOutreach'
import { MarketingChannelsHome } from './MarketingChannels/MarketingChannelsHome'
import { SeasonProgressHeader } from './Overview/SeasonProgressHeader'
import { TokensAndAutomationsHome } from './TokensAndAutomations/TokensAndAutomationsHome'
import { RenewalsHome } from './Renewals/RenewalsHome'
import { OccupancyHome } from './Occupancy/OccupancyHome'
import {
    PersonFileProvider,
    usePersonFile,
} from './PersonFile/PersonFileContext'
import { formatSeasonDateRange } from './utils/seasonDates'
import { resolveEffectiveCurrentSeasonId } from './utils/seasonSelection'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: theme.palette.background.default,
    },
    rootFill: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    pageHeader: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: theme.spacing(3),
    },
    headerLeft: {
        justifySelf: 'start',
        minWidth: 0,
        overflow: 'hidden',
    },
    headerCenter: {
        justifySelf: 'stretch',
        width: '100%',
        minWidth: 0,
    },
    headerRight: {
        justifySelf: 'end',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        minWidth: 0,
        paddingLeft: theme.spacing(2.5),
        borderLeft: `1px solid ${theme.palette.divider}`,
    },
    tokenMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: theme.palette.text.secondary,
    },
    tokenIcon: {
        fontSize: 16,
    },
    tokenLabel: {
        fontSize: '0.75rem',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    titleIcon: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    identityText: {
        minWidth: 0,
    },
    identityMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    seasonTitle: {
        fontSize: '0.95rem',
        fontWeight: 600,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    seasonDates: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    historicalChip: {
        height: 22,
        flexShrink: 0,
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    headerActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        gap: theme.spacing(0.75),
    },
    manageSeasonsButton: {
        textTransform: 'none',
        fontSize: '0.8rem',
        padding: theme.spacing(0.5, 1.25),
        width: '100%',
        justifyContent: 'center',
    },
    tabsWrapper: {
        padding: theme.spacing(1.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
    },
    tabs: {
        width: '100%',
        minHeight: 'unset',
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5),
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minHeight: 40,
        minWidth: 0,
        maxWidth: 'none',
        flex: 1,
        borderRadius: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        opacity: 1,
        transition:
            'color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main + '18',
            boxShadow: 'none',
        },
        '&.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main + '24',
        },
    },
    tabLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
    tabIcon: {
        fontSize: 18,
    },
    content: {
        padding: theme.spacing(3),
        width: '100%',
        boxSizing: 'border-box',
    },
    contentFlushTop: {
        paddingTop: 0,
    },
    contentFill: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingBottom: 0,
    },
    readOnlyContent: {
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: 0.92,
    },
    historicalBanner: {
        margin: theme.spacing(0, 3),
        padding: theme.spacing(1, 2),
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    historicalBannerLink: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}))

type TabValue = 0 | 1 | 2 | 3 | 4 | 5 | 6

const LEASING_SEASON_TABS = [
    { label: 'Prospects', icon: SchoolIcon, slug: 'prospects' },
    {
        label: 'Marketing Channels',
        icon: ContactMailIcon,
        slug: 'marketing-channels',
    },
    { label: 'Campaigns', icon: AnnouncementOutlinedIcon, slug: 'campaigns' },
    { label: 'Leads Pipeline', icon: AccountTreeIcon, slug: 'pipeline' },
    { label: 'Renewals', icon: AutorenewIcon, slug: 'renewals' },
    { label: 'Occupancy', icon: HotelIcon, slug: 'occupancy' },
    { label: 'Tokens & Automations', icon: TuneIcon, slug: 'tokens' },
] as const

/** Legacy / alternate query values → tab index. */
const TAB_SLUG_ALIASES: Record<string, TabValue> = {
    overview: 0,
    prospects: 0,
    colleges: 0,
    'marketing-channels': 1,
    campaigns: 2,
    outreach: 2,
    'lead-generation': 2,
    pipeline: 3,
    renewals: 4,
    occupancy: 5,
    tokens: 6,
}

function parseTabFromSearch(search: string): TabValue {
    const tab = new URLSearchParams(search).get('tab')
    if (!tab) return 0
    return TAB_SLUG_ALIASES[tab] ?? 0
}

function tabSlugForIndex(index: TabValue): string | null {
    if (index === 0) return null
    return LEASING_SEASON_TABS[index].slug
}

function FindPersonButton({ className }: { className: string }) {
    const { openFindPerson } = usePersonFile()
    return (
        <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<SearchIcon fontSize="small" />}
            className={className}
            onClick={openFindPerson}
        >
            Find person
        </Button>
    )
}

type LeasingSeasonLocationState = {
    seasonId?: number
}

export const LeasingSeasonHome: React.FC = () => {
    const classes = useStyles()
    const location = useLocation<LeasingSeasonLocationState>()
    const history = useHistory()
    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined
    const leasingSeason = useLeasingSeason(apartmentId)
    const [currentTab, setCurrentTab] = useState<TabValue>(() =>
        parseTabFromSearch(location.search),
    )

    useEffect(() => {
        setCurrentTab(parseTabFromSearch(location.search))
    }, [location.search])

    const {
        seasons,
        hasSeason,
        isListLoading,
        isDetailLoading,
        setSelectedSeasonId,
        isViewingCurrentSeason,
        selectedSeasonId,
        seasonDetail,
        funnelMetrics,
        monthlyTokens,
        tokenBalance,
        progress,
        lastYearPercentLeased,
    } = leasingSeason

    useEffect(() => {
        if (isListLoading) return

        if (!hasSeason) {
            history.replace(Routes.manage.leasingSeason.seasons)
            return
        }

        const stateSeasonId = location.state?.seasonId
        if (stateSeasonId != null) {
            if (seasons.some((s) => s.id === stateSeasonId)) {
                setSelectedSeasonId(stateSeasonId)
            }
            return
        }

        const currentId = resolveEffectiveCurrentSeasonId(seasons)
        if (currentId != null) {
            setSelectedSeasonId(currentId)
        }
    }, [
        hasSeason,
        history,
        isListLoading,
        location.state?.seasonId,
        seasons,
        setSelectedSeasonId,
    ])

    const tokenLabel =
        monthlyTokens != null
            ? `${monthlyTokens.toLocaleString()} tokens / month`
            : 'Tokens'

    const handleTabChange = (
        _event: React.ChangeEvent<{}>,
        newValue: TabValue,
    ) => {
        setCurrentTab(newValue)

        const params = new URLSearchParams(location.search)
        const slug = tabSlugForIndex(newValue)
        if (slug == null) {
            params.delete('tab')
        } else {
            params.set('tab', slug)
        }
        const search = params.toString()
        history.replace({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
            state: location.state,
        })
    }

    const isReadOnly = !isViewingCurrentSeason
    const isPipelineTab = currentTab === 3

    const selectedSeasonListItem = seasons.find(
        (s) => s.id === selectedSeasonId,
    )

    const headerSeasonName = seasonDetail?.name ?? selectedSeasonListItem?.name

    const seasonDateRange = formatSeasonDateRange(
        seasonDetail?.start_date ?? selectedSeasonListItem?.start_date,
        seasonDetail?.end_date ?? selectedSeasonListItem?.end_date,
    )

    if (isListLoading || !hasSeason) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="50vh"
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <PersonFileProvider
            apartmentId={apartmentId}
            seasonId={selectedSeasonId}
            readOnly={isReadOnly}
        >
            <Box
                className={`${classes.root} ${
                    isPipelineTab ? classes.rootFill : ''
                }`}
            >
                <Box className={classes.pageHeader}>
                    <Box className={classes.headerLeft}>
                        <Box className={classes.titleRow}>
                            <Box className={classes.titleIcon}>
                                <EventNoteIcon fontSize="small" />
                            </Box>
                            <Box className={classes.identityText}>
                                {headerSeasonName && (
                                    <Typography className={classes.seasonTitle}>
                                        {headerSeasonName}
                                    </Typography>
                                )}
                                <Box className={classes.identityMeta}>
                                    {seasonDateRange && (
                                        <Typography
                                            className={classes.seasonDates}
                                        >
                                            {seasonDateRange}
                                        </Typography>
                                    )}
                                    {!isViewingCurrentSeason && (
                                        <Chip
                                            size="small"
                                            label="Historical — read only"
                                            className={classes.historicalChip}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.headerCenter}>
                        {progress && (
                            <SeasonProgressHeader
                                metrics={progress}
                                lastYearPercent={lastYearPercentLeased}
                            />
                        )}
                    </Box>

                    <Box className={classes.headerRight}>
                        {isViewingCurrentSeason && (
                            <Box className={classes.tokenMeta}>
                                <MonetizationOnIcon
                                    className={classes.tokenIcon}
                                />
                                <Typography className={classes.tokenLabel}>
                                    {tokenLabel}
                                </Typography>
                            </Box>
                        )}
                        <Box className={classes.headerActions}>
                            <Button
                                component={Link}
                                to={Routes.manage.leasingSeason.seasons}
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={
                                    <CalendarTodayIcon fontSize="small" />
                                }
                                className={classes.manageSeasonsButton}
                            >
                                Manage Seasons
                            </Button>
                            <FindPersonButton
                                className={classes.manageSeasonsButton}
                            />
                        </Box>
                    </Box>
                </Box>

                {isReadOnly && headerSeasonName && (
                    <Box className={classes.historicalBanner} mt={1.5}>
                        Viewing <strong>{headerSeasonName}</strong> as a
                        historical snapshot. To work on your active season, go
                        to{' '}
                        <Link
                            to={Routes.manage.leasingSeason.seasons}
                            className={classes.historicalBannerLink}
                        >
                            Manage Seasons
                        </Link>
                        .
                    </Box>
                )}

                <Box className={classes.tabsWrapper}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        className={classes.tabs}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                    >
                        {LEASING_SEASON_TABS.map((tab, index) => {
                            const Icon = tab.icon
                            return (
                                <Tab
                                    key={tab.label}
                                    className={classes.tab}
                                    label={
                                        <Box className={classes.tabLabel}>
                                            <Icon className={classes.tabIcon} />
                                            <span>{tab.label}</span>
                                        </Box>
                                    }
                                    value={index}
                                />
                            )
                        })}
                    </Tabs>
                </Box>

                <Box
                    className={`${classes.content} ${
                        currentTab === 6 ? classes.contentFlushTop : ''
                    } ${isPipelineTab ? classes.contentFill : ''} ${
                        isReadOnly && currentTab !== 1
                            ? classes.readOnlyContent
                            : ''
                    }`}
                >
                    {isDetailLoading ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {currentTab === 0 && (
                                <CollegesYouServe
                                    readOnly={isReadOnly}
                                    funnelMetrics={funnelMetrics}
                                />
                            )}
                            {currentTab === 1 && (
                                <MarketingChannelsHome
                                    seasonId={selectedSeasonId}
                                />
                            )}
                            {currentTab === 2 && (
                                <LeasingSeasonOutreach
                                    seasonId={selectedSeasonId}
                                    funnelMetrics={funnelMetrics}
                                    primaryCampaigns={
                                        seasonDetail?.primary_campaigns
                                    }
                                />
                            )}
                            {currentTab === 3 && (
                                <LeadsPipelineHome
                                    seasonId={selectedSeasonId}
                                    apartmentId={apartmentId}
                                    readOnly={isReadOnly}
                                />
                            )}
                            {currentTab === 4 && (
                                <RenewalsHome
                                    readOnly={isReadOnly}
                                    leasingSeasonId={
                                        leasingSeason.selectedSeasonId ??
                                        undefined
                                    }
                                />
                            )}
                            {currentTab === 5 && <OccupancyHome />}
                            {currentTab === 6 && (
                                <TokensAndAutomationsHome
                                    leasingSeason={leasingSeason}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </PersonFileProvider>
    )
}
